import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { MessageSquare, Menu, X, ChevronDown } from 'lucide-react';

const Navigation = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const [dropdownTimeoutId, setDropdownTimeoutId] = useState(null);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isProductsActive = () => {
    return location.pathname.startsWith('/products');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleProductsMouseEnter = () => {
    // Clear any existing timeout
    if (dropdownTimeoutId) {
      clearTimeout(dropdownTimeoutId);
      setDropdownTimeoutId(null);
    }
    setIsProductsDropdownOpen(true);
  };

  const handleProductsMouseLeave = () => {
    // Set a longer delay to give users more time to move to the dropdown
    const timeoutId = setTimeout(() => {
      setIsProductsDropdownOpen(false);
    }, 300);
    setDropdownTimeoutId(timeoutId);
  };

  const handleProductsClick = () => {
    // Clear any timeout and toggle dropdown
    if (dropdownTimeoutId) {
      clearTimeout(dropdownTimeoutId);
      setDropdownTimeoutId(null);
    }
    setIsProductsDropdownOpen(!isProductsDropdownOpen);
  };

  const handleDropdownMouseEnter = () => {
    // Clear timeout when mouse enters dropdown area
    if (dropdownTimeoutId) {
      clearTimeout(dropdownTimeoutId);
      setDropdownTimeoutId(null);
    }
    setIsProductsDropdownOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    // Close dropdown when mouse leaves dropdown area
    const timeoutId = setTimeout(() => {
      setIsProductsDropdownOpen(false);
    }, 200);
    setDropdownTimeoutId(timeoutId);
  };

  return (
    <nav className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_vioq-content-update/artifacts/b8s7g90b_Smart%20AI%20Solutions%20Logo.png"
                alt="Smart AI Solutions"
                className="h-12 w-auto"
              />
            </div>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-lg font-medium transition-colors ${
                isActive('/') 
                  ? 'text-[#ff7a1f]' 
                  : 'text-gray-700 hover:text-[#ff7a1f]'
              }`}
            >
              Home
            </Link>
            
            <Link 
              to="/about" 
              className={`text-lg font-medium transition-colors ${
                isActive('/about') 
                  ? 'text-[#ff7a1f]' 
                  : 'text-gray-700 hover:text-[#ff7a1f]'
              }`}
            >
              About
            </Link>
            
            {/* VioQbase Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsProductsDropdownOpen(true)}
              onMouseLeave={() => setIsProductsDropdownOpen(false)}
            >
              <button
                className={`flex items-center space-x-1 text-lg font-medium transition-colors ${
                  isActive('/features') || isActive('/use-cases') || isActive('/products/vioqbase')
                    ? 'text-[#ff7a1f]' 
                    : 'text-gray-700 hover:text-[#ff7a1f]'
                }`}
                onClick={() => setIsProductsDropdownOpen(!isProductsDropdownOpen)}
              >
                <span>VioQbase</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${
                  isProductsDropdownOpen ? 'rotate-180' : ''
                }`} />
              </button>
              
              {/* VioQbase Dropdown Menu */}
              {isProductsDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-0 w-72 bg-white rounded-lg shadow-2xl border border-gray-200 py-3 z-50 animate-fade-in"
                >
                  <Link
                    to="/products/vioqbase"
                    className="block px-5 py-4 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#ff7a1f] transition-colors border-b border-gray-100"
                    onClick={() => setIsProductsDropdownOpen(false)}
                  >
                    <div className="font-semibold text-base">VioQbase Knowledge Base</div>
                    <div className="text-xs text-gray-500 mt-1">Voice-Powered Enterprise Knowledge</div>
                  </Link>
                  <Link
                    to="/features"
                    className="block px-5 py-4 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#ff7a1f] transition-colors border-b border-gray-100"
                    onClick={() => setIsProductsDropdownOpen(false)}
                  >
                    <div className="font-semibold text-base">Features</div>
                    <div className="text-xs text-gray-500 mt-1">VioQbase Features & Capabilities</div>
                  </Link>
                  <Link
                    to="/use-cases"
                    className="block px-5 py-4 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#ff7a1f] transition-colors"
                    onClick={() => setIsProductsDropdownOpen(false)}
                  >
                    <div className="font-semibold text-base">Use Cases</div>
                    <div className="text-xs text-gray-500 mt-1">Real-World Applications</div>
                  </Link>
                </div>
              )}
            </div>

            <Link 
              to="/products/vioreceptionist" 
              className={`text-lg font-medium transition-colors ${
                isActive('/products/vioreceptionist') 
                  ? 'text-[#ff7a1f]' 
                  : 'text-gray-700 hover:text-[#ff7a1f]'
              }`}
            >
              VioReceptionist
            </Link>

            <Link 
              to="/products/vioconcierge" 
              className={`text-lg font-medium transition-colors ${
                isActive('/products/vioconcierge') 
                  ? 'text-[#ff7a1f]' 
                  : 'text-gray-700 hover:text-[#ff7a1f]'
              }`}
            >
              VioConcierge
            </Link>

            <Link 
              to="/products/viocoach" 
              className={`text-lg font-medium transition-colors ${
                isActive('/products/viocoach') 
                  ? 'text-[#ff7a1f]' 
                  : 'text-gray-700 hover:text-[#ff7a1f]'
              }`}
            >
              VioCoach
            </Link>

            <Link 
              to="/hr-consultancy" 
              className={`text-lg font-medium transition-colors ${
                isActive('/hr-consultancy') 
                  ? 'text-[#ff7a1f]' 
                  : 'text-gray-700 hover:text-[#ff7a1f]'
              }`}
            >
              HR Consultancy
            </Link>
            
            <Link 
              to="/contact" 
              className={`text-lg font-medium transition-colors ${
                isActive('/contact') 
                  ? 'text-[#ff7a1f]' 
                  : 'text-gray-700 hover:text-[#ff7a1f]'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-2">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-700 hover:text-[#ff7a1f] transition-colors p-3 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-6 border-t border-gray-200 bg-white">
            <div className="flex flex-col space-y-2 pt-6 px-4">
              <Link 
                to="/" 
                className={`text-lg font-medium transition-colors px-4 py-3 min-h-[44px] rounded-lg flex items-center ${
                  isActive('/') 
                    ? 'text-[#ff7a1f] bg-orange-50' 
                    : 'text-gray-700 hover:text-[#ff7a1f] hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
              
              <Link 
                to="/features" 
                className={`text-lg font-medium transition-colors px-4 py-3 min-h-[44px] rounded-lg flex items-center ${
                  isActive('/features') 
                    ? 'text-[#ff7a1f] bg-orange-50' 
                    : 'text-gray-700 hover:text-[#ff7a1f] hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Features
              </Link>
              <Link 
                to="/use-cases" 
                className={`text-lg font-medium transition-colors px-4 py-3 min-h-[44px] rounded-lg flex items-center ${
                  isActive('/use-cases') 
                    ? 'text-[#ff7a1f] bg-orange-50' 
                    : 'text-gray-700 hover:text-[#ff7a1f] hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Use Cases
              </Link>
              <Link 
                to="/about" 
                className={`text-lg font-medium transition-colors px-4 py-3 min-h-[44px] rounded-lg flex items-center ${
                  isActive('/about') 
                    ? 'text-[#ff7a1f] bg-orange-50' 
                    : 'text-gray-700 hover:text-[#ff7a1f] hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About
              </Link>
              
              {/* Mobile Products Section */}
              <div className="border-t border-gray-100 pt-3 mt-3">
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide px-4 py-2">Other Products</div>
                <Link
                  to="/products/vioreceptionist"
                  className="text-lg font-medium transition-colors px-4 py-3 min-h-[44px] rounded-lg flex items-center text-gray-700 hover:text-[#ff7a1f] hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  VioReceptionist
                </Link>
                <Link
                  to="/products/vioconcierge"
                  className="text-lg font-medium transition-colors px-4 py-3 min-h-[44px] rounded-lg flex items-center text-gray-700 hover:text-[#ff7a1f] hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  VioConcierge
                </Link>
                <Link
                  to="/products/viocoach"
                  className="text-lg font-medium transition-colors px-4 py-3 min-h-[44px] rounded-lg flex items-center text-gray-700 hover:text-[#ff7a1f] hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  VioCoach
                </Link>
              </div>
              
              <Link 
                to="/hr-consultancy" 
                className={`text-lg font-medium transition-colors px-4 py-3 min-h-[44px] rounded-lg flex items-center ${
                  isActive('/hr-consultancy') 
                    ? 'text-[#ff7a1f] bg-orange-50' 
                    : 'text-gray-700 hover:text-[#ff7a1f] hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                HR Consultancy
              </Link>
              
              <Link 
                to="/contact" 
                className={`text-lg font-medium transition-colors px-4 py-3 min-h-[44px] rounded-lg flex items-center ${
                  isActive('/contact') 
                    ? 'text-[#ff7a1f] bg-orange-50' 
                    : 'text-gray-700 hover:text-[#ff7a1f] hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact
              </Link>
              
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;